import React, { useState, useEffect, useMemo } from "react";
import "./PastNotes.css";
import { useSelector } from "react-redux";
import { selectTimerStatus } from "../../sessionWatchSlice";
import MindMapViewer from "../../../../../mind-2/MindMapViewer";

export const PastNotes = () => {
    const timerStatus = useSelector(selectTimerStatus);
    const [notesByDate, setNotesByDate] = useState({});
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedSession, setSelectedSession] = useState('all');
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [viewingMindMapNote, setViewingMindMapNote] = useState(null);

    // Load notes from localStorage
    useEffect(() => {
        const loadNotes = () => {
            const allKeys = Object.keys(localStorage);
            const notes = {};

            allKeys.forEach(key => {
                if (key.startsWith("notes_")) {
                    const date = key.replace("notes_", "");
                    const notesData = JSON.parse(localStorage.getItem(key)) || [];
                    if (notesData.length > 0) {
                        notes[date] = notesData;
                    }
                }
            });

            setNotesByDate(notes);

            // Auto-select most recent date
            const dates = Object.keys(notes).sort().reverse();
            if (dates.length > 0 && !selectedDate) {
                setSelectedDate(dates[0]);
            }
        };

        loadNotes();
    }, [timerStatus, refreshTrigger]);

    // Parse note into structured format
    const parseNote = (note) => {
        // Handle new object format
        if (typeof note === 'object' && note !== null) {
            const parts = (note.note || "").split("-");
            return {
                content: parts[0] || "",
                duration: parseInt(parts[1]) || 0,
                location: parts[2] || "Unknown",
                mindMap: note.mindMap || null,
                timestamp: note.timestamp || null
            };
        }
        // Handle legacy string format
        const parts = note.split("-");
        return {
            content: parts[0] || "",
            duration: parseInt(parts[1]) || 0,
            location: parts[2] || "Unknown",
            mindMap: null,
            timestamp: null
        };
    };

    // Get available dates sorted (newest first)
    const availableDates = useMemo(() => {
        return Object.keys(notesByDate).sort().reverse();
    }, [notesByDate]);

    // Format date for display
    const formatDate = (dateStr) => {
        const date = new Date(dateStr + "T00:00:00");
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // Get notes for selected date
    const selectedDateNotes = useMemo(() => {
        if (!selectedDate || !notesByDate[selectedDate]) return [];
        return notesByDate[selectedDate].map((note, index) => ({
            ...parseNote(note),
            index,
            rawNote: note
        }));
    }, [selectedDate, notesByDate]);

    // Extract time from note (using index as proxy since we don't have exact time)
    const extractTime = (noteIndex, totalNotes) => {
        // Since we don't have exact timestamps, create proxy times based on order
        // Spread throughout the day
        const hour = 8 + Math.floor((noteIndex / totalNotes) * 12);
        const minute = (noteIndex * 15) % 60;
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour > 12 ? hour - 12 : hour;
        return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
    };

    // Get sessions for dropdown
    const sessionsForDate = useMemo(() => {
        return selectedDateNotes.map((note, idx) => ({
            time: extractTime(idx, selectedDateNotes.length),
            location: note.location,
            duration: note.duration,
            index: note.index
        }));
    }, [selectedDateNotes]);

    // Get displayed notes based on filter
    const displayedNotes = useMemo(() => {
        if (selectedSession === 'all') {
            return selectedDateNotes;
        }
        const sessionIndex = parseInt(selectedSession);
        return selectedDateNotes.filter(note => note.index === sessionIndex);
    }, [selectedDateNotes, selectedSession]);

    // Calculate total duration
    const totalDuration = useMemo(() => {
        return displayedNotes.reduce((sum, note) => sum + note.duration, 0);
    }, [displayedNotes]);

    // Handle date change
    const handleDateChange = (e) => {
        setSelectedDate(e.target.value);
        setSelectedSession('all');
    };

    // Handle session filter change
    const handleSessionChange = (e) => {
        setSelectedSession(e.target.value);
    };

    // Handle delete note
    const handleDeleteNote = (noteIndex) => {
        if (!window.confirm("Delete this note?")) return;

        const key = `notes_${selectedDate}`;
        const currentNotes = JSON.parse(localStorage.getItem(key)) || [];
        currentNotes.splice(noteIndex, 1);

        if (currentNotes.length === 0) {
            localStorage.removeItem(key);
            // Remove date from state
            const newNotesByDate = { ...notesByDate };
            delete newNotesByDate[selectedDate];
            setNotesByDate(newNotesByDate);

            // Select next available date
            const remainingDates = Object.keys(newNotesByDate).sort().reverse();
            if (remainingDates.length > 0) {
                setSelectedDate(remainingDates[0]);
            } else {
                setSelectedDate(null);
            }
        } else {
            localStorage.setItem(key, JSON.stringify(currentNotes));
            setRefreshTrigger(prev => prev + 1);
        }

        setSelectedSession('all');
    };

    // No notes state
    if (availableDates.length === 0) {
        return (
            <div className="past-notes-container">
                <div className="past-notes-header">
                    <h2 className="past-notes-title">🗂️ Past Notes</h2>
                </div>
                <div className="past-notes-empty">
                    <p>No notes recorded yet.</p>
                    <p>Start a session to create notes!</p>
                </div>
            </div>
        );
    }

    return (
        <div className="past-notes-container">
            <div className="past-notes-header">
                <h2 className="past-notes-title">🗂️ Past Notes</h2>
            </div>

            {/* Filter Bar */}
            <div className="past-notes-filter-bar">
                <div className="filter-group">
                    <label htmlFor="date-select" className="filter-label">Date:</label>
                    <select
                        id="date-select"
                        className="filter-dropdown"
                        value={selectedDate || ""}
                        onChange={handleDateChange}
                        aria-label="Select date to view notes"
                    >
                        {availableDates.map(date => (
                            <option key={date} value={date}>
                                {formatDate(date)}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label htmlFor="session-select" className="filter-label">Session:</label>
                    <select
                        id="session-select"
                        className="filter-dropdown"
                        value={selectedSession}
                        onChange={handleSessionChange}
                        disabled={!selectedDate}
                        aria-label="Filter notes by session time"
                    >
                        <option value="all">All Sessions ({selectedDateNotes.length})</option>
                        {sessionsForDate.length > 0 && <option disabled>───────────</option>}
                        {sessionsForDate.map((session, idx) => (
                            <option key={idx} value={session.index}>
                                {session.time} - {session.location} ({session.duration}s)
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Summary Stats */}
            <div className="past-notes-summary">
                Showing {displayedNotes.length} note{displayedNotes.length !== 1 ? 's' : ''} • {totalDuration} seconds total
            </div>

            {/* Notes Display */}
            <div className="past-notes-content">
                {displayedNotes.map((note, idx) => {
                    const displayTime = extractTime(note.index, selectedDateNotes.length);
                    const hasMindMap = note.mindMap && note.mindMap.nodes;

                    return (
                        <div key={note.index} className={`note-card ${hasMindMap ? 'has-mindmap' : ''}`}>
                            <div className="note-card-header">
                                <div className="note-card-meta">
                                    <span className="note-time-icon">🕐</span>
                                    <span className="note-time">{displayTime}</span>
                                    <span className="note-separator">•</span>
                                    <span className="note-location">{note.location}</span>
                                    <span className="note-separator">•</span>
                                    <span className="note-duration">{note.duration}s</span>
                                    {hasMindMap && (
                                        <>
                                            <span className="note-separator">•</span>
                                            <span
                                                className="note-mindmap-badge"
                                                title="Click to view mind map"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setViewingMindMapNote(note);
                                                }}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                🧠 Mind Map
                                            </span>
                                        </>
                                    )}
                                </div>
                                <button
                                    className="note-delete-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteNote(note.index);
                                    }}
                                    aria-label={`Delete note from ${displayTime} at ${note.location}`}
                                >
                                    ×
                                </button>
                            </div>
                            <div className="note-card-content">
                                {note.content.split('\n').map((line, lineIdx) => (
                                    <React.Fragment key={lineIdx}>
                                        {line}
                                        {lineIdx < note.content.split('\n').length - 1 && <br />}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Mind Map Modal */}
            {viewingMindMapNote && viewingMindMapNote.mindMap && (
                <MindMapViewer
                    nodes={viewingMindMapNote.mindMap.nodes}
                    onClose={() => setViewingMindMapNote(null)}
                />
            )}
        </div>
    );
};
